import { StrictMode, useEffect, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { api, session, setUnauthorizedHandler, type BetLimit } from '@/lib/api'
import { BoardPage } from '@/pages/BoardPage'
import { BetLimitPage } from '@/pages/BetLimitPage'
import { Disconnected } from '@/pages/Disconnected'
import { WindowPage } from '@/pages/WindowPage'

/**
 * Each reference page carries its own inline <style> after the shared stylesheets; these are
 * those, moved to files and added per page in the same order.
 */
function useStyles(hrefs: string[]) {
  const key = hrefs.join('|')
  useEffect(() => {
    const links = key.split('|').filter(Boolean).map((href) => {
      const l = document.createElement('link')
      l.rel = 'stylesheet'
      l.href = href
      document.head.appendChild(l)
      return l
    })
    return () => links.forEach((l) => l.remove())
  }, [key])
}

const POPPINS = 'https://fonts.googleapis.com/css?family=Poppins:700'
const ECZAR = 'https://fonts.googleapis.com/css?family=Eczar:700'
const EXTRA = '/assets/css/keno-extra.css'

/** /launch?token= — take the session the partner launch issued, then drop it from the URL. */
function consumeLaunch(): void {
  if (window.location.pathname !== '/launch') return
  const token = new URLSearchParams(window.location.search).get('token')
  if (token) {
    session.clear()
    session.setToken(token)
  }
  window.history.replaceState(null, '', '/')
}

/**
 * /impersonate?token= — the console's "เข้าในนาม" for KENO. The token is the API's one-time,
 * 60-second impersonation token, not a session: redeem it for one (same-origin /api), keep
 * only the access token, and drop the URL. A failed redeem leaves no session — the
 * disconnected panel shows.
 */
async function consumeImpersonate(): Promise<void> {
  if (window.location.pathname !== '/impersonate') return
  const token = new URLSearchParams(window.location.search).get('token')
  window.history.replaceState(null, '', '/')
  session.clear()
  if (!token) return
  try {
    const res = await fetch('/api/auth/impersonate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
      cache: 'no-store',
    })
    if (!res.ok) return
    const body = (await res.json()) as { accessToken?: unknown }
    if (typeof body.accessToken === 'string') session.setToken(body.accessToken)
  } catch { /* no session → disconnected */ }
}

function App() {
  const [disconnected, setDisconnected] = useState(() => !session.token())
  // The limit chosen in this tab. A stored one is re-checked against what this member's
  // agent offers NOW — an admin or master may have switched it off since it was picked.
  const [limit, setLimit] = useState<BetLimit | null>(session.limit)
  const [limitChecked, setLimitChecked] = useState(() => session.limit() === null)
  useEffect(() => {
    const stored = session.limit()
    if (!stored || disconnected) return
    let live = true
    api.betLimits()
      .then(({ limits }) => {
        if (!live) return
        const still = limits.find((l) => l.id === stored.id)
        if (still) { session.setLimit(still); setLimit(still) } else { session.clearLimit(); setLimit(null) }
      })
      .catch(() => { /* offline: keep it — a bet is re-checked on the server anyway */ })
      .finally(() => { if (live) setLimitChecked(true) })
    return () => { live = false }
  }, [disconnected])
  // A trailing slash is the same page: browsers cached nginx's 301 /rules → /rules/ from
  // when a public/rules/ directory shadowed the route, and still ask for /rules/.
  const path = window.location.pathname.replace(/\/+$/, '') || '/'
  const isWindow = ['/results', '/statement', '/statement-day', '/rules', '/announcement'].includes(path)

  useEffect(() => { setUnauthorizedHandler(() => setDisconnected(true)) }, [])

  const announcement = path === '/announcement'
  useStyles(
    disconnected ? ['/assets/css/disconnected.css']
      : announcement ? []
        : isWindow ? [ECZAR, '/assets/css/icons/dripicons.min.css', '/assets/css/vendor/bootstrap-datepicker/bootstrap-datepicker.min.css', ...(path === '/statement-day' ? ['/assets/wlapi/gameresult.css'] : []), `/assets/css/window-${path.slice(1)}.css`, EXTRA]
          : limit === null ? ['/assets/css/betlimit.css']
            : [POPPINS, '/assets/css/keno-page.css', '/assets/css/disconnected.css', EXTRA],
  )

  // The reference announcement window is a bare, unstyled page.
  useEffect(() => {
    if (!announcement) return
    const base = [...document.querySelectorAll<HTMLLinkElement>('head link[rel="stylesheet"]')]
    base.forEach((l) => { l.disabled = true })
    return () => base.forEach((l) => { l.disabled = false })
  }, [announcement])

  if (disconnected) return <Disconnected />
  if (isWindow) return <WindowPage kind={path.slice(1) as 'results' | 'statement' | 'statement-day' | 'rules' | 'announcement'} />
  if (!limitChecked) return null
  if (limit === null) {
    return <BetLimitPage onChoose={(l) => { session.setLimit(l); setLimit(l) }} />
  }
  return <BoardPage limit={limit} onDisconnected={() => setDisconnected(true)} />
}

consumeLaunch()
void consumeImpersonate().then(() => {
  const root = document.getElementById('root')
  if (root) createRoot(root).render(
    <StrictMode>
      <App />
    </StrictMode>,
  )
})
